import React from "react";
import { Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StorageIcon from "@mui/icons-material/Storage";
import HubIcon from "@mui/icons-material/Hub";
import AppsIcon from "@mui/icons-material/Apps";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import color from "../Constants/colors";

export type ToolPresentation = {
  label: string;
  icon: React.ReactElement;
  chip: {
    bgcolor: string;
    color: string;
    borderColor: string;
    iconColor: string;
  };
};

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function humanizeToolId(input: string): string {
  return input
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function getToolPresentation(toolIdOrName: string): ToolPresentation {
  const id = toolIdOrName.toLowerCase();

  const unifiedChip = {
    bgcolor: color.gray50,
    color: color.gray700,
    borderColor: color.gray200,
    iconColor: color.gray600,
  };

  const presets: Record<
    string,
    Omit<ToolPresentation, "label"> & { label?: string }
  > = {
    web_search: { icon: <SearchIcon sx={{ fontSize: 14 }} />, chip: unifiedChip },
    document_read: {
      icon: <DescriptionIcon sx={{ fontSize: 14 }} />,
      chip: unifiedChip,
    },
    document_generation: {
      icon: <AutoAwesomeIcon sx={{ fontSize: 14 }} />,
      chip: unifiedChip,
    },
    retrieve_knowledge_base_data: {
      icon: <MenuBookIcon sx={{ fontSize: 14 }} />,
      chip: unifiedChip,
    },
    retrieve_entity_data: {
      icon: <StorageIcon sx={{ fontSize: 14 }} />,
      chip: unifiedChip,
    },
    platform_features: {
      icon: <AppsIcon sx={{ fontSize: 14 }} />,
      chip: unifiedChip,
    },
    // MCP tools are shown as "conn: tool"
    mcp: { icon: <HubIcon sx={{ fontSize: 14 }} />, chip: unifiedChip },
  };

  const presetKey = Object.keys(presets).find((k) => id === k);
  if (presetKey) {
    return { label: humanizeToolId(toolIdOrName), ...presets[presetKey] };
  }

  // Heuristic for MCP tool labels like "conn: something"
  if (toolIdOrName.includes(":")) {
    return { label: toolIdOrName, ...presets.mcp };
  }

  return {
    label: humanizeToolId(toolIdOrName),
    icon: <SupportAgentRoundedIcon sx={{ fontSize: 14 }} />,
    chip: unifiedChip,
  };
}

const agentIconSizeMap = {
  sm: { px: 20, svg: "small" as const },
  md: { px: 24, svg: "medium" as const },
  lg: { px: 40, svg: "large" as const },
};

/** Agent avatar in UI: server `icon` URL when set, otherwise default bot icon. */
export function AgentIcon({
  name,
  iconUrl,
  size,
}: {
  name: string;
  iconUrl?: string | null;
  size: "sm" | "md" | "lg";
}): React.ReactElement {
  const dim = agentIconSizeMap[size];
  const url = iconUrl?.trim();
  if (url) {
    return (
      <Box
        component="img"
        src={url}
        alt={name}
        sx={{
          width: dim.px,
          height: dim.px,
          objectFit: "contain",
          display: "block",
        }}
      />
    );
  }
  return <SupportAgentRoundedIcon sx={{ fontSize: dim.svg }} />;
}
